import { useCallback, useEffect, useState } from 'react'
import { adminMutate, apiGet, usePermissions, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { Link } from '../router.js'
import { jourFr, heureFr } from './lib.js'
import { nuitFr } from './nuit-billetteries-model.js'
import { EVT_RATTACHEMENTS } from './rattachement-model.js'
import { PartAssociation, lireParts, type PartsAssociations } from '../finance/PartAssociation.js'
import { signalerRattachements } from './Rattachements.js'

/**
 * LA NUIT (ET LA PART) DE CHAQUE ÉVÉNEMENT D'ASSOCIATION (16/10/2026).
 *
 * Déménagée le 20/10/2026 de Réglages Soirées › Billetteries vers
 * Exploitation › Associations › À décider (décision de l'exploitant) : ce
 * sont des décisions de tous les jours, pas des réglages. Réglages ›
 * Billetteries garde un lien vers ici.
 *
 * Gestion range chaque événement Pretix dans la nuit où ses billets ont été
 * scannés ; avant le premier scan, d'après l'heure de fin annoncée, puis de
 * début. La correction est un geste ATOMIQUE, motivé et tracé (bouton
 * explicite, jamais une Save Bar). La part de l'association par billet est
 * une règle de la Comptabilité : elle n'apparaît qu'avec ses droits à elle.
 *
 * La liste se relit après chaque rattachement (`EVT_RATTACHEMENTS`) : un
 * rattachement à une autre nuit change la nuit d'un événement.
 */

interface SoireeAsso {
  id: string
  name: string
  billetterie: string
  starts_at: string
  ends_at: string | null
  status: 'SCHEDULED' | 'CANCELLED'
  scanned: number | null
  valid: number | null
  nuit: string
  auto_night: string | null
  auto_basis_label: string | null
  nuit_du_debut: string
  manual_night: string | null
  manual_reason: string | null
  manual_by: string | null
  manual_at: string | null
}

const fmtDT = (iso: string) => new Date(iso).toLocaleString('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
})

/** Une ligne de la liste : la nuit retenue, d'où elle vient, et le geste pour la corriger. */
function LigneAsso({ s, motifMin, peutGerer, onFait, parts, peutRegler, onPart }: {
  s: SoireeAsso; motifMin: number; peutGerer: boolean; onFait: () => void
  parts: PartsAssociations | null; peutRegler: boolean; onPart: () => void
}) {
  const toast = useToast()
  const [ouvert, setOuvert] = useState<'corriger' | 'rendre' | null>(null)
  const [nuit, setNuit] = useState(s.nuit)
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const envoyer = async () => {
    setEnvoi(true); setErreur(null)
    try {
      const r = await adminMutate<{ ok?: boolean; nuit?: string; message?: string; error?: string }>('PUT', `/api/v1/soirees/${s.id}/night`, {
        night: ouvert === 'rendre' ? null : nuit, reason: motif.trim(),
      })
      if (r.status !== 200) { setErreur(r.data.message ?? 'Correction impossible.'); return }
      toast('ok', ouvert === 'rendre'
        ? `« ${s.name} » suit de nouveau sa nuit automatique.`
        : `« ${s.name} » compte désormais dans la ${nuitFr(nuit).toLowerCase()}.`)
      setOuvert(null); setMotif(''); onFait()
    } finally { setEnvoi(false) }
  }

  const motifCourt = motif.trim().length < motifMin
  return (
    <div className="so-asso">
      <span className="so-asso__nom">{s.name}</span>
      {peutGerer && ouvert === null ? (
        <span className="so-asso__actions">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { setNuit(s.nuit); setOuvert('corriger') }}>Corriger la nuit</button>
          {s.manual_night ? <button type="button" className="lc-btn lc-btn--ghost" onClick={() => setOuvert('rendre')}>Rendre à l’automatique</button> : null}
        </span>
      ) : <span />}
      <span className="so-asso__faits">
        <span>Annoncé {jourFr(s.starts_at).toLowerCase()} à {heureFr(s.starts_at)} · {s.billetterie}</span>
        <span><strong>{s.scanned ?? '—'}</strong> entrées · {s.valid ?? '—'} billets</span>
        <span>Nuit retenue : <strong>{nuitFr(s.nuit).replace(/^Nuit du /, '')}</strong>
          {s.manual_night ? ' — corrigée à la main' : s.auto_basis_label ? ` — ${s.auto_basis_label}` : ''}</span>
        {s.nuit !== s.nuit_du_debut && !s.manual_night ? (
          <span className="so-asso__ecart">La date saisie dans la billetterie la rangerait {nuitFr(s.nuit_du_debut).replace(/^Nuit du /, 'le ')}.</span>
        ) : null}
        {s.manual_night ? (
          <span>Par {s.manual_by ?? 'un compte supprimé'}{s.manual_at ? ` le ${fmtDT(s.manual_at)}` : ''} : « {s.manual_reason} »
            {s.auto_night && s.auto_night !== s.manual_night ? ` (automatique : ${nuitFr(s.auto_night).replace(/^Nuit du /, '')})` : ''}</span>
        ) : null}
      </span>
      {/* La part : une ligne pleine largeur, hors de la rangée de faits (dans
          un <span> en flex, son formulaire s'y glissait de travers). */}
      {parts !== null ? (
        <div className="so-asso__part">
          <PartAssociation evenement={{ id: s.id, name: s.name }} parts={parts} peutRegler={peutRegler} onFait={onPart} />
        </div>
      ) : null}
      {ouvert ? (
        <div className="so-asso__form">
          {ouvert === 'corriger' ? (
            <Field label="Nuit de la soirée" helper="La nuit commerciale : une entrée à 2 h du matin compte dans la nuit de la veille.">
              <TextInput type="date" value={nuit} onChange={setNuit} ariaLabel={`Nuit de ${s.name}`} />
            </Field>
          ) : <span className="lc-hint">La nuit redeviendra celle qu’indiquent les entrées scannées.</span>}
          <Field label="Pourquoi" helper={`Quelques mots (${motifMin} caractères au moins) : la correction est tracée et sera relue.`} error={erreur}>
            <TextInput value={motif} onChange={setMotif} maxLength={300} placeholder="Soirée décalée au vendredi par le BDE" ariaLabel="Motif de la correction" />
          </Field>
          <span className="so-asso__actions">
            <button type="button" className="lc-btn lc-btn--primary" disabled={envoi || motifCourt || (ouvert === 'corriger' && !/^\d{4}-\d{2}-\d{2}$/.test(nuit))}
              onClick={() => { void envoyer() }}>
              {envoi ? 'Enregistrement…' : ouvert === 'rendre' ? 'Rendre à l’automatique' : 'Corriger la nuit'}
            </button>
            <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { setOuvert(null); setErreur(null) }}>Annuler</button>
          </span>
        </div>
      ) : null}
    </div>
  )
}

/** La liste complète : chargée ici, relue après chaque rattachement ou correction. */
export function NuitsDesEvenements() {
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const peutLireParts = permissions.permissions.includes('finance.reference.read')
  const peutReglerParts = permissions.permissions.includes('finance.settings.manage')
  const [assos, setAssos] = useState<SoireeAsso[] | null>(null)
  const [erreur, setErreur] = useState(false)
  const [motifMin, setMotifMin] = useState(8)
  const [parts, setParts] = useState<PartsAssociations | null>(null)
  const chargerAssos = useCallback(() => {
    void apiGet<{ soirees?: SoireeAsso[]; motif_min?: number }>('/api/v1/soirees/associations')
      .then((j) => {
        if (!Array.isArray(j.soirees)) { setErreur(true); return }
        setAssos(j.soirees); setMotifMin(j.motif_min ?? 8); setErreur(false)
      }).catch(() => setErreur(true))
  }, [])
  const chargerParts = useCallback(() => {
    void apiGet<Parameters<typeof lireParts>[0]>('/api/v1/finance/associations/shares')
      .then((j) => setParts(lireParts(j))).catch(() => setParts(null))
  }, [])
  useEffect(() => {
    chargerAssos()
    window.addEventListener(EVT_RATTACHEMENTS, chargerAssos)
    return () => window.removeEventListener(EVT_RATTACHEMENTS, chargerAssos)
  }, [chargerAssos])
  useEffect(() => { if (peutLireParts) chargerParts() }, [peutLireParts, chargerParts])

  return (
    <>
      <p className="lc-hint" style={{ marginTop: 0 }}>
        {peutLireParts ? 'Chaque événement porte aussi la part que son association récupère par billet vendu : la Comptabilité la retire de la valeur faciale quand la billetterie des associations compte dans ses chiffres. ' : ''}
        Gestion range chaque événement dans la nuit où ses billets ont été scannés ; avant le premier scan, d’après
        l’heure de fin annoncée, puis de début. Corrigez-la si elle est fausse : le motif est demandé, la correction
        est tracée, et elle tient même quand la billetterie est relue.
      </p>
      {assos === null ? (erreur ? (
        <p className="lc-hint" role="alert">
          La liste n’a pas pu être lue : aucune nuit ne se corrige tant qu’elle ne s’affiche pas.{' '}
          <button type="button" className="lc-btn lc-btn--ghost" onClick={chargerAssos}>Réessayer</button>
        </p>
      ) : <p className="lc-hint">Chargement…</p>)
        : assos.length === 0 ? (
          <p className="lc-hint">
            Aucun événement d’association pour l’instant. Ils apparaissent ici dès que la lecture de Pretix est
            allumée (<Link href="/settings/integrations/pretix">Intégrations → Pretix</Link>).
          </p>
        ) : (
          <div className="so-assos">
            {assos.map((s) => (
              <LigneAsso key={`${s.id}-${s.nuit}-${s.manual_at ?? ''}`} s={s} motifMin={motifMin} peutGerer={peutGerer} onFait={() => { chargerAssos(); signalerRattachements() }}
                parts={peutLireParts ? parts : null} peutRegler={peutReglerParts} onPart={chargerParts} />
            ))}
          </div>
        )}
    </>
  )
}

/** Le titre de la section : la part n'est nommée que pour qui la lit. */
export function titreNuitsDesEvenements(peutLireParts: boolean): string {
  return peutLireParts ? 'La nuit et la part de chaque événement d’association' : 'La nuit de chaque événement d’association'
}
